import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Browser } from '@capacitor/browser';

import { useTheme } from '@mui/material/styles';
import { Alert, AlertTitle, Box, Button, Stack, Typography, useMediaQuery } from '@mui/material';
import { MIcon } from '../@material-extend';
import LoadingScreen from '../loadings/LoadingScreen';
import { GenericModal } from '../modals';

import { getFileData } from '../../redux/slices/files';

const OpenDocument = ({ object, entityType, open, onOpen }) => {
  const theme = useTheme();
  const smallDevice = useMediaQuery(theme.breakpoints.down('md'));
  const [fileItem, setFileItem] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (object.id && entityType) {
      setLoading(true);
      getFileData(object.filepath)
        .then((response) => {
          setFileItem(response.file);
          setLoading(false);
        })
        .catch(() => {
          setFileItem({});
          setLoading(false);
        });
    }
  }, [object.id, object.filepath, entityType]);

  const fileRewriteUrl = (item) => {
    if (!item.url) {
      return '';
    }
    switch (item.extension) {
      case 'rtf':
      case 'docx':
      case 'doc':
      case 'xls':
      case 'xlsx':
      case 'xlsxm':
      case 'ppt':
      case 'pptx':
      case 'ppsx':
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(item.url)}`;
      default:
        return `${item.url}#zoom=100&toolbar=1&statusbar=1&navpanes=1`;
    }
  };

  function downloadFile(item) {
    if (!item.url) {
      return false;
    }

    if (window.env.isApp) {
      Browser.open({ url: item.url });
    } else {
      window.open(item.url, '_blank');
    }
    return true;
  }

  // Loading et desktop
  if (loading && !smallDevice) {
    return (
      <Box sx={{ height: '90vh', overflow: 'hidden' }}>
        <LoadingScreen />
      </Box>
    );
  }

  // Loading et smallDevice
  if (loading && smallDevice) {
    return;
  }

  const message = (
    <Alert severity={object.code ? 'warning' : 'info'}>
      {object.code ? <AlertTitle>Document absent</AlertTitle> : 'Aucune facture trouvée'}
      {entityType === 'invoice' && object.code ? 'Aucun document trouvé associé à la facture' : null}
      {object.code ? <strong> {object.code} </strong> : null}
      {object.date ? `au ${moment(object.date).format('DD/MM/YYYY')}` : null}.
    </Alert>
  );

  // Mobile, document not found and open : alert message in modal
  if (smallDevice && Object.keys(fileItem).length === 0 && open) {
    return (
      <GenericModal title="Détail de la facture" fullScreen open={open} handleClose={() => onOpen(false)}>
        {message}
      </GenericModal>
    );
  }

  // Mobile, document found and open : download it
  if (smallDevice && fileItem && fileItem.url && open) {
    return downloadFile(fileItem);
  }

  // Mobile and not open
  if (smallDevice && !open) {
    return;
  }

  // Desktop, document not found : alert message in right screen
  if (!smallDevice && Object.keys(fileItem).length === 0) {
    return message;
  }

  // Desktop : document found, display it on right screen
  return (
    <Box id="open-document-container" sx={{ height: '90vh', overflow: 'hidden' }}>
      <Stack id="open-document-header" sx={{ flexDirection: 'row', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" gutterBottom component="div">
          Détail {entityType === 'invoice' ? 'de la facture' : 'du document'} {object.code} du{' '}
          {moment(object.date).format('DD/MM/YYYY')}.
        </Typography>
        <Button variant="contained" startIcon={<MIcon icon="download" filled />} onClick={() => downloadFile(fileItem)}>
          Télécharger
        </Button>
      </Stack>

      <iframe title="documentComete" src={fileRewriteUrl(fileItem)} height="90%" width="100%" />
    </Box>
  );
};

OpenDocument.propTypes = {
  object: PropTypes.object,
  entityType: PropTypes.string,
  open: PropTypes.bool,
  onOpen: PropTypes.func
};

export default OpenDocument;
